'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth/context';

const PING_INTERVAL_MS = 60_000;

/**
 * Invisible heartbeat for logged-in users: POSTs /api/activity/ping every
 * ~60s while the tab is visible. Feeds admin DAU/MAU/time-spent metrics.
 * Never fires for guests or on /admin pages, and stops for the session if
 * the server reports the activity_tracking flag is off.
 */
export default function ActivityPinger() {
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();
  const disabledRef = useRef(false);
  const isAdminPage = pathname?.startsWith('/admin') ?? false;

  useEffect(() => {
    if (!isAuthenticated || isAdminPage || disabledRef.current) return;

    let cancelled = false;

    const ping = async () => {
      if (cancelled || disabledRef.current) return;
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch('/api/activity/ping', { method: 'POST', keepalive: true });
        if (res.ok) {
          const body = await res.json().catch(() => null);
          if (body && body.enabled === false) {
            disabledRef.current = true;
          }
        }
      } catch {
        // Fire-and-forget: never surface errors to the user.
      }
    };

    ping();
    const interval = setInterval(ping, PING_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, isAdminPage]);

  return null;
}
